import { HttpErrorResponse } from '@angular/common/http';
import { AfterViewInit, Component, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
    IOcbCULocalAgent,
    IOcbLocalAgent,
    LocalAgentsService,
    OcbAgentAuthenticationModes,
    OcbAgentStatus,
    OcbAgentTypes,
    UserService
} from '@app/core';
import { TranslateService } from '@ngx-translate/core';
import { DxDataGridComponent } from 'devextreme-angular/ui/data-grid';
import { DxValidationGroupComponent } from 'devextreme-angular/ui/validation-group';
import { confirm } from 'devextreme/ui/dialog';
import notify from 'devextreme/ui/notify';
import { forkJoin, from as fromPromise, Observable } from 'rxjs';
import { filter, finalize, mergeMap, tap } from 'rxjs/operators';
import { LocalAgentCheckConnectionComponent } from '../../shared';

@Component({
    selector: 'app-local-agents',
    templateUrl: './local-agents.component.html',
    styleUrls: ['./local-agents.component.scss'],
})
export class LocalAgentsComponent implements AfterViewInit {

    @ViewChild('localAgents')
    public laGrid: DxDataGridComponent;
    @ViewChild('localAgentValidation')
    public laValidationGroup: DxValidationGroupComponent;
    @ViewChild('checkConnectionPopup')
    checkConnectionPopup: LocalAgentCheckConnectionComponent;

    // Main Data-Grid
    public dataSource: IOcbLocalAgent[] = null;

    // Popup Display
    public isDisplayPopupVisible: boolean = false;
    public displayedLocalAgent: IOcbLocalAgent = null;

    // Popup New & Edit
    public isEditPopupVisible: boolean = false;
    public selectedLocalAgent: IOcbLocalAgent = null;
    public isNew: boolean = null;

    public isLoadPanelVisible: boolean = false;

    private currentEnvironmentId: string = '';
    private statusLabels: any;
    private agentAuthenticationModesLabels: any;
    private agentTypesLabels: any;

    constructor(
        private route: ActivatedRoute,
        private userService: UserService,
        private localAgentsService: LocalAgentsService,
        private translateService: TranslateService,
        private router: Router) {
        this.getLocalAgentAuthenticationMode = this.getLocalAgentAuthenticationMode.bind(this);
        this.getLocalAgentStatus = this.getLocalAgentStatus.bind(this);

        this.displayAgentAuthenticationMode = this.displayAgentAuthenticationMode.bind(this);
        this.displayDate = this.displayDate.bind(this);
        this.displayLastContactDate = this.displayLastContactDate.bind(this);
        this.displayStatusUpdatedDate = this.displayStatusUpdatedDate.bind(this);
    }

    public ngAfterViewInit(): void {
        this.route.params.subscribe(_ => {
            this.currentEnvironmentId = this.route.snapshot.params.id;
            forkJoin([
                this.translateService.get('status'),
                this.translateService.get('agentTypes'),
                this.translateService.get('agentAuthenticationModes')
            ])
            .subscribe(labels => {
                this.statusLabels = labels[0];
                this.agentTypesLabels = labels[1];
                this.agentAuthenticationModesLabels = labels[2];
            });
            this.loadDataSource();
        });
    }

    /**
     * Set Recipients context menu items
     * @param e Handle event
     */
    public onContextMenuPreparing(e) {
        if (e.row && e.row.rowType === 'data') {
            forkJoin([
                this.translateService.get('_commons.edit'),
                this.translateService.get('_commons.start'),
                this.translateService.get('_commons.stop'),
                this.translateService.get('_commons.disable'),
                this.translateService.get('_commons.enable'),
                this.translateService.get('_commons.connectionCheck'),
                this.translateService.get('_commons.seeCommandsHistory'),
                this.translateService.get('mainLayout.executionTriggers'),
                this.translateService.get('mainLayout.maintenances')
            ]
            ).subscribe({
                next: (messages) => {
                    e.items = [];
                    e.items.push({ text: messages[0], icon: 'edit', onItemClick: (ev) => this.displayUpdatePopup(ev, e.row.data) });
                    if (!e.row.data.isDisabled) {
                        if (e.row.data.currentStatus === OcbAgentStatus.Stopped) 
                            e.items.push({ text: messages[1], icon: 'video', beginGroup: true, onItemClick: (ev) => this.startLocalAgent(ev, e.row.data.id) });
                        else if (e.row.data.currentStatus === OcbAgentStatus.Started)
                            e.items.push({ text: messages[2], icon: 'square', beginGroup: true, onItemClick: (ev) => this.stopLocalAgent(ev, e.row.data.id) });

                        e.items.push({ text: messages[3], icon: 'remove', onItemClick: (ev) => this.disableLocalAgent(ev, e.row.data) });
                        e.items.push({ text: messages[7], icon: 'clock', beginGroup: true, onItemClick: (ev) => this.router.navigateByUrl(`/${e.row.data.environmentId}/local-agents-execution-triggers?agent=${e.row.data.id}`) });
                        e.items.push({ text: messages[8], icon: 'clock', onItemClick: (ev) => this.router.navigateByUrl(`/${e.row.data.environmentId}/local-agents-maintenances?agent=${e.row.data.id}`) });
                        e.items.push({ text: messages[6], icon: 'clock', beginGroup: true, onItemClick: (ev) => this.router.navigateByUrl(`/${e.row.data.environmentId}/local-agents-commands?agent=${e.row.data.id}`) });
                        e.items.push({ text: messages[5], icon: 'check', beginGroup: true, onItemClick: (ev) => this.showCheckConnectionPopup(e.row.data) });
                    }
                    else {
                        e.items.push({ text: messages[4], icon: 'check', beginGroup: true, onItemClick: (ev) => this.enableLocalAgent(ev, e.row.data) });
                    }
                },
                error: (err) => this.handleError(err)
            });
        }
    }

    public onRowPrepared(e): void {
        if (e.data != null && e.data.isDisabled === true)
            e.rowElement.bgColor = '#FFCECE';
    }

    public displayNewPopup(): void {
        this.selectedLocalAgent = this.getNewIOcbLocalAgent();
        this.isNew = true;
        this.isEditPopupVisible = true;
    }

    public displayUpdatePopup(e, localAgent: IOcbLocalAgent): void {
        e.event.stopPropagation();
        this.selectedLocalAgent = this.getIOcbLocalAgentFromData(localAgent);
        this.isNew = false;
        this.isEditPopupVisible = true;
    }

    public createLocalAgent() {
        if (this.laValidationGroup.instance.validate().isValid) {
            this.isLoadPanelVisible = true;
            const newLocalAgent = this.getIOcbCULocalAgentFromData(this.selectedLocalAgent);
            this.localAgentsService.addLocalAgent(newLocalAgent)
                .pipe(
                    mergeMap(_ => this.translateService.get('localAgents.msgLocalAgentCreationComplete')),
                    finalize(() => this.isLoadPanelVisible = false))
                .subscribe({
                    next: (message) => {
                        this.isEditPopupVisible = false;
                        this.loadDataSource();
                        notify(message, 'success', 3000);
                    },
                    error: (err) => this.handleError(err)
                });
        }
    }

    public updateLocalAgent() {
        if (this.laValidationGroup.instance.validate().isValid) {
            this.isLoadPanelVisible = true;
            const updateLocalAgent = this.getIOcbCULocalAgentFromData(this.selectedLocalAgent);
            this.localAgentsService.updateLocalAgent(this.selectedLocalAgent.id, updateLocalAgent)
                .pipe(
                    mergeMap(_ => this.translateService.get('localAgents.msgLocalAgentUpdateComplete')),
                    finalize(() => this.isLoadPanelVisible = false))
                .subscribe({
                    next: (message) => {
                        this.isEditPopupVisible = false;
                        this.loadDataSource();
                        notify(message, 'success', 3000);
                    },
                    error: (err) => this.handleError(err)
                });
        }
    }

    public deleteLocalAgent(e, localAgentId) {
        e.event.stopPropagation();
        forkJoin([
            this.translateService.get('localAgents.msgDeleteConfirm'),
            this.translateService.get('localAgents.msgDeleteConfirmTitle')
        ])
            .pipe(
                // Display confirm popup
                mergeMap(messages => fromPromise(confirm(messages[0], messages[1]))),
                filter(accepted => accepted),
                // Display loader
                tap(_ => this.isLoadPanelVisible = true),
                // Send the RESET request
                mergeMap(_ => this.localAgentsService.deleteLocalAgent(localAgentId)),
                // Display complete message
                mergeMap(_ => this.translateService.get('localAgents.msgLocalAgentDeleteComplete')),
                // Hide loader
                finalize(() => this.isLoadPanelVisible = false))
            .subscribe({
                next: (message) => {
                    this.loadDataSource();
                    notify(message, 'success', 3000);
                },
                error: (err) => this.handleError(err)
            });
    }

    public startLocalAgent(e, localAgentId) {
        e.event.stopPropagation();
        forkJoin([
            this.translateService.get('localAgents.msgStartConfirm'),
            this.translateService.get('localAgents.msgStartConfirmTitle')
        ])
            .pipe(
                // Display confirm popup
                mergeMap(messages => fromPromise(confirm(messages[0], messages[1]))),
                filter(accepted => accepted),
                // Display loader
                tap(_ => this.isLoadPanelVisible = true),
                // Send the request
                mergeMap(_ => this.localAgentsService.start(localAgentId, true)),
                // Display confirm message
                mergeMap(_ => this.translateService.get('localAgents.msgStartComplete')),
                // Hide loader
                finalize(() => this.isLoadPanelVisible = false))
            .subscribe({
                next: (message) => {
                    this.loadDataSource();
                    notify(message, 'success', 3000);
                },
                error: (err) => this.handleError(err)
            });
    }

    public stopLocalAgent(e, localAgentId) {
        e.event.stopPropagation();
        forkJoin([
            this.translateService.get('localAgents.msgStopConfirm'),
            this.translateService.get('localAgents.msgStopConfirmTitle')
        ])
            .pipe(
                // Display confirm popup
                mergeMap(messages => fromPromise(confirm(messages[0], messages[1]))),
                filter(accepted => accepted),
                // Display loader
                tap(_ => this.isLoadPanelVisible = true),
                // Send the RESET request
                mergeMap(_ => this.localAgentsService.stop(localAgentId, true)),
                // Display confirm message
                mergeMap(_ => this.translateService.get('localAgents.msgStopComplete')),
                // Hide loader
                finalize(() => this.isLoadPanelVisible = false))
            .subscribe({
                next: (message) => {
                    this.loadDataSource();
                    notify(message, 'success', 3000);
                },
                error: (err) => this.handleError(err)
            });
    }

    public disableLocalAgent(e, localAgent) {
        e.event.stopPropagation();
        const disableLocalAgent = this.getIOcbCULocalAgentFromData(localAgent);
        disableLocalAgent.isDisabled = true;
        forkJoin([
            this.translateService.get('localAgents.msgDisableConfirm'),
            this.translateService.get('localAgents.msgDisableConfirmTitle')
        ])
            .pipe(
                // Display confirm popup
                mergeMap(messages => fromPromise(confirm(messages[0], messages[1]))),
                filter(accepted => accepted),
                // Display loader
                tap(_ => this.isLoadPanelVisible = true),
                // Send the RESET request
                mergeMap(_ => this.localAgentsService.updateLocalAgent(localAgent.id, disableLocalAgent)),
                // Display confirm message
                mergeMap(_ => this.translateService.get('localAgents.msgDisableComplete')),
                // Hide loader
                finalize(() => this.isLoadPanelVisible = false))
            .subscribe({
                next: (message) => {
                    this.loadDataSource();
                    notify(message, 'success', 3000);
                },
                error: (err) => this.handleError(err)
            });
    }

    public enableLocalAgent(e, localAgent) {
        e.event.stopPropagation();
        const enableLocalAgent = this.getIOcbCULocalAgentFromData(localAgent);
        enableLocalAgent.isDisabled = false;
        forkJoin([
            this.translateService.get('localAgents.msgEnableConfirm'),
            this.translateService.get('localAgents.msgEnableConfirmTitle')
        ])
            .pipe(
                // Display confirm popup
                mergeMap(messages => fromPromise(confirm(messages[0], messages[1]))),
                filter(accepted => accepted),
                // Display loader
                tap(_ => this.isLoadPanelVisible = true),
                // Send the RESET request
                mergeMap(_ => this.localAgentsService.updateLocalAgent(localAgent.id, enableLocalAgent)),
                // Display confirm message
                mergeMap(_ => this.translateService.get('localAgents.msgEnableComplete')),
                // Hide loader
                finalize(() => this.isLoadPanelVisible = false))
            .subscribe({
                next: (message) => {
                    this.loadDataSource();
                    notify(message, 'success', 3000);
                },
                error: (err) => this.handleError(err)
            });
    }

    public displayLocalAgent(e) {
        this.displayedLocalAgent = e.data;
        this.isDisplayPopupVisible = true;
    }

    public getLocalAgentAuthenticationMode(rowData) {
        return this.agentAuthenticationModesLabels[OcbAgentAuthenticationModes[rowData.authenticationMode]];
    }

    public getLocalAgentStatus(rowData) {
        return this.statusLabels[OcbAgentStatus[rowData.currentStatus]];
    }

    public displayAgentAuthenticationMode(rowData) {
        return this.agentAuthenticationModesLabels[OcbAgentAuthenticationModes[rowData.authenticationMode]];
    }

    public displayAgentType(rowData) {
        return this.agentTypesLabels[OcbAgentTypes[rowData.agentType]];
    }

    public displayDate(date: string): string {
        let currentLocale = 'en-US';
        let currentDate = new Date(date);
        switch (this.userService.language) {
            case 'en': currentLocale = 'en-US'; break;
            case 'fr': currentLocale = 'fr-FR'; currentDate = new Date(date + 'Z'); break;
        }
        return date ? currentDate.toLocaleString(currentLocale) : '';
    }

    public displayStatusUpdatedDate(rowData): string {
        if (!rowData?.statusUpdatedOn) {
            return '';
        }
        let currentLocale = 'en-US';
        let currentDate = new Date(rowData.statusUpdatedOn);
        switch (this.userService.language) {
            case 'en': currentLocale = 'en-US'; break;
            case 'fr': currentLocale = 'fr-FR'; currentDate = new Date(rowData.statusUpdatedOn + 'Z'); break;
        }
        return rowData.statusUpdatedOn ? currentDate.toLocaleString(currentLocale) : '';
    }

    public displayLastContactDate(rowData): string {
        if (!rowData?.lastContactOn) {
            return '';
        }
        let currentLocale = 'en-US';
        let currentDate = new Date(rowData.lastContactOn);
        switch (this.userService.language) {
            case 'en': currentLocale = 'en-US'; break;
            case 'fr': currentLocale = 'fr-FR'; currentDate = new Date(rowData.lastContactOn + 'Z'); break;
        }
        return rowData.lastContactOn ? currentDate.toLocaleString(currentLocale) : '';
    }

    private loadDataSource(): void {
        this.showLoading$('_commons.loading')
            .pipe(
                mergeMap(_ => this.localAgentsService.getLocalAgents(this.currentEnvironmentId, true)),
                finalize(() => this.laGrid.instance.endCustomLoading()))
            .subscribe({
                next: (res) => this.dataSource = res,
                error: (err) => this.handleError(err)
            });
    }

    private showLoading$(messageKey: string): Observable<void> {
        return this.translateService.get(messageKey).pipe(
            tap(message => this.laGrid.instance.beginCustomLoading(`${message}...`)),
        );
    }

    private getNewIOcbLocalAgent(): IOcbLocalAgent {
        return {
            id: '',
            environmentId: '',
            environmentName: '',
            name: '',
            agentType: OcbAgentTypes.OnPremise,
            authenticationMode: OcbAgentAuthenticationModes.OcbToken,
            isDisabled: false,
            currentStatus: null,
            currentAssemblyVersion: '',
            currentAssemblyFileVersion: '',
            currentAssemblyInformationalVersion: '',
            serverName: '',
            statusUpdatedOn: null,
            versionsUpdatedOn: null
        };
    }

    private getIOcbLocalAgentFromData(data: IOcbLocalAgent): IOcbLocalAgent {
        return {
            id: data.id,
            environmentId: data.environmentId,
            environmentName: data.environmentName,
            name: data.name,
            agentType: data.agentType,
            authenticationMode: data.authenticationMode,
            entraId: data.entraId,
            publicIpAddresses: data.publicIpAddresses,
            isDisabled: data.isDisabled,
            currentStatus: data.currentStatus,
            currentAssemblyVersion: data.currentAssemblyVersion,
            currentAssemblyFileVersion: data.currentAssemblyFileVersion,
            currentAssemblyInformationalVersion: data.currentAssemblyInformationalVersion,
            serverName: data.serverName,
            statusUpdatedOn: data.statusUpdatedOn,
            versionsUpdatedOn: data.versionsUpdatedOn
        };
    }

    private getIOcbCULocalAgentFromData(data: IOcbLocalAgent): IOcbCULocalAgent {
        return {
            environmentId: data.environmentId ? data.environmentId : this.currentEnvironmentId,
            name: data.name,
            isDisabled: data.isDisabled,
            agentType: data.agentType,
            authenticationMode: data.authenticationMode,
            entraId: data.entraId,
            publicIpAddresses: data.publicIpAddresses
        };
    }

    private showCheckConnectionPopup(agent: IOcbLocalAgent) {
        this.checkConnectionPopup.agentId = agent.id;
        this.checkConnectionPopup.visible = true;
    }

    private handleError(error: any): void {
        let message: '';
        if (error instanceof HttpErrorResponse) {
            if (error.error.hasOwnProperty('message')) {
                message = error.error.message;
            } else if (error.error.hasOwnProperty('name')) {
                message = error.error.name;
            } else {
                message = error.error;
            }
        } else {
            message = error;
        }

        notify(message, 'error', 3000);
    }
}
